import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import StatCard from '../../components/ui/StatCard';
import GradientIcon from '../../components/ui/GradientIcon';
import { CheckCircle2, Layers, ListChecks, BookOpen } from 'lucide-react';
import GaugeMeter from '../../components/ui/GaugeMeter';
import QRCodeGenerator from '../../components/session/QRCodeGenerator';
import PreSessionAnalyticsTab from '../../components/trainer/PreSessionAnalyticsTab';
import { trainerService, sessionService } from '../../services';
import api from '../../services/api';
import { notifications } from '../../utils/toast';
import { usePreferences } from '../../contexts/PreferencesContext';
import { getActivityIcon, getActivityTypeColor } from '../../utils/activityIcons';
import {
  getMetricBandInfo,
  TRAINER_INTERPRETATIONS,
  calculateARS as calcARS
} from '../../utils/reportingConstants';

/**
 * Trainer Session Detail Page
 * Shows session details for trainers with tabs: Overview, Sub-sessions
 */

const TrainerSessionDetailPage = () => {
  const navigate = useNavigate();

  // Solo trainers (individual orgs) own their sessions and may edit the details.
  const soloTrainer = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return (u.primaryOrganization || u.organization)?.type === 'individual';
    } catch { return false; }
  })();
  const { sessionId } = useParams();
  const { formatDateWithTimezone, formatTime } = usePreferences();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [earlyStartModal, setEarlyStartModal] = useState({ show: false, timeRemaining: '' });
  const [showQRModal, setShowQRModal] = useState(false);
  // Phase 2: Pre-session analytics state
  const [aiReport, setAiReport] = useState(null);
  const [aiReportLoading, setAiReportLoading] = useState(false);
  const [aiHistory, setAiHistory] = useState([]);
  const [aiHistoryLoading, setAiHistoryLoading] = useState(false);

  useEffect(() => {
    const fetchSessionData = async () => {
      try {
        setLoading(true);
        const response = await trainerService.getSession(sessionId);
        if (response.success) {
          setSession(response.data);
        }
      } catch (error) {
        console.error('Error fetching session:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSessionData();
  }, [sessionId]);


  // Auto-fetch AI report history when AI tab is selected
  useEffect(() => {
    if (activeTab === 'ai-report' && aiHistory.length === 0 && !aiHistoryLoading && sessionId) {
      const fetchAiHistory = async () => {
        setAiHistoryLoading(true);
        try {
          const res = await api.get(`/ai/reports/session/${sessionId}/history`);
          if (res.success) setAiHistory(res.data);
        } catch (err) {
          console.error('Error loading AI report history:', err);
        } finally {
          setAiHistoryLoading(false);
        }
      };
      fetchAiHistory();
    }
  }, [activeTab, sessionId]);

  // Activity helper functions
  const getActivityLabel = (type) => {
    const labels = {
      quiz: 'Quiz',
      poll: 'Poll',
      wordCloud: 'Word Cloud',
      challenges: 'Challenges',
      actionPlans: 'Action Plans',
      openQuestion: 'Open Question',
      feedback: 'Feedback',
    };
    return labels[type] || type;
  };

  const handleStartSession = async () => {
    // Check if session has a scheduled start time
    const scheduledStart = session?.startTime || session?.scheduledStartTime || session?.scheduledDate;

    if (scheduledStart) {
      const startTime = new Date(scheduledStart);

      // Times are stored as pseudo-UTC (local time with Z suffix)
      // We need to compare with current local time in the same format
      // Create pseudo-UTC "now" by getting current local time components directly
      const now = new Date();
      const localYear = now.getFullYear();
      const localMonth = String(now.getMonth() + 1).padStart(2, '0');
      const localDay = String(now.getDate()).padStart(2, '0');
      const localHours = String(now.getHours()).padStart(2, '0');
      const localMinutes = String(now.getMinutes()).padStart(2, '0');
      const localSeconds = String(now.getSeconds()).padStart(2, '0');

      // Create pseudo-UTC from local time components (same format as stored times)
      const pseudoUtcNow = new Date(`${localYear}-${localMonth}-${localDay}T${localHours}:${localMinutes}:${localSeconds}.000Z`);

      // If current time is before the scheduled start time
      if (pseudoUtcNow < startTime) {
        const diffMs = startTime - pseudoUtcNow;
        const diffMins = Math.ceil(diffMs / 60000);
        const hours = Math.floor(diffMins / 60);
        const mins = diffMins % 60;

        let timeRemaining = '';
        if (hours > 0) {
          timeRemaining = `${hours} hour${hours > 1 ? 's' : ''} and ${mins} minute${mins !== 1 ? 's' : ''}`;
        } else {
          timeRemaining = `${mins} minute${mins !== 1 ? 's' : ''}`;
        }

        setEarlyStartModal({
          show: true,
          timeRemaining,
          scheduledTime: formatTime(startTime),
        });
        return;
      }
    }

    // Proceed with starting the session
    try {
      const response = await trainerService.launchSession(sessionId);
      if (response.success) {
        notifications.success('Session started successfully!');
        // Navigate to session control page
        navigate(`/trainer/session/${sessionId}/control`);
      } else {
        notifications.error(response.error || 'Failed to start session');
      }
    } catch (error) {
      console.error('Error starting session:', error);
      notifications.error(error.message || 'Failed to start session. Please try again.');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Session Details">
        <div className="p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!session) {
    return (
      <DashboardLayout title="Session Details">
        <div className="p-6">
          <p className="text-neutral-600">Session not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    ...(session?.preSession?.enabled ? [{ id: 'pre-session', label: 'Pre-Session' }] : []),
    { id: 'analytics', label: 'Analytics' },
    { id: 'subsessions', label: 'Sub-sessions' },
    { id: 'ai-report', label: 'AI Impact' },
  ];

  // Calculate session-level metrics for Analytics tab
  const calculateSessionMetrics = () => {
    const metrics = {
      trs: { value: 0, band: null },
      tei: { value: 0, band: null },
      pci: { value: 0, band: null },
      lai: { value: 0, band: null },
      cri: { value: 0, band: null },
      ari: { value: 0, band: null },
      totalChallenges: 0,
      totalActionPlans: 0,
      totalResponses: 0,
      engagementRate: 0,
      hasData: false
    };

    if (!session?.subSessions) return metrics;

    const allQ1 = [], allQ2 = [], allQ3 = [], allQ4 = [], allQ5 = [];
    const allARS = [];

    session.subSessions.forEach(subSession => {
      subSession.activities?.forEach(activity => {
        const responses = activity.responses || [];

        if (activity.type === 'feedback') {
          responses.forEach(r => {
            const resp = r.response || r;
            if (resp.topicRelevance) allQ1.push(resp.topicRelevance);
            if (resp.trainerEffectiveness) allQ2.push(resp.trainerEffectiveness);
            if (resp.confidenceToApply) allQ3.push(resp.confidenceToApply);
            if (resp.learningAdvocacy) allQ4.push(resp.learningAdvocacy);
            if (resp.challengeResolution) allQ5.push(resp.challengeResolution);
            // Legacy rating
            if (resp.rating && !resp.topicRelevance) {
              allQ1.push(resp.rating);
              allQ2.push(resp.rating);
              allQ3.push(resp.rating);
              allQ4.push(resp.rating);
              allQ5.push(resp.rating);
            }
          });
        }

        // Use Participant-based response counts when available
        const actId = (activity._id || activity.id)?.toString();
        const accurateCount = (session.activityResponseCounts && actId)
          ? (session.activityResponseCounts[actId] || 0)
          : responses.length;

        if (activity.type === 'challenges') {
          metrics.totalChallenges += accurateCount;
        }

        if (activity.type === 'actionPlans') {
          metrics.totalActionPlans += accurateCount;
          responses.forEach(r => {
            const resp = r.response || r;
            if (resp.byWhen && resp.commitment) {
              allARS.push(calcARS(resp.byWhen, resp.commitment));
            }
          });
        }

        metrics.totalResponses += accurateCount;
      });
    });

    // Calculate averages
    const avg = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

    if (allQ1.length > 0) {
      metrics.trs.value = Math.round(avg(allQ1) * 100) / 100;
      metrics.trs.band = getMetricBandInfo('trs', metrics.trs.value);
      metrics.hasData = true;
    }
    if (allQ2.length > 0) {
      metrics.tei.value = Math.round(avg(allQ2) * 100) / 100;
      metrics.tei.band = getMetricBandInfo('tei', metrics.tei.value);
      metrics.hasData = true;
    }
    if (allQ3.length > 0) {
      metrics.pci.value = Math.round(avg(allQ3) * 100) / 100;
      metrics.pci.band = getMetricBandInfo('pcs', metrics.pci.value);
      metrics.hasData = true;
    }
    if (allQ4.length > 0) {
      metrics.lai.value = Math.round(avg(allQ4) * 100) / 100;
      metrics.lai.band = getMetricBandInfo('lai', metrics.lai.value);
      metrics.hasData = true;
    }
    if (allQ5.length > 0) {
      metrics.cri.value = Math.round(avg(allQ5) * 100) / 100;
      metrics.cri.band = getMetricBandInfo('cri', metrics.cri.value);
      metrics.hasData = true;
    }
    if (allARS.length > 0) {
      metrics.ari.value = Math.round(avg(allARS) * 100) / 100;
      metrics.ari.band = getMetricBandInfo('ars', metrics.ari.value);
      metrics.hasData = true;
    }

    // Calculate engagement rate (capped at 100%)
    // Only count activities that have been launched (participants had a chance to respond)
    const launchedActivities = session.subSessions.reduce((sum, s) =>
      sum + (s.activities?.filter(a =>
        a.status === 'launched' || a.status === 'completed' || a.status === 'ended' || a.status === 'active'
      ).length || 0), 0);
    const totalParticipants = session.participants?.filter(p => p.status === 'joined').length || 0;
    const maxResponses = launchedActivities * totalParticipants;
    metrics.engagementRate = maxResponses > 0 ? Math.min(100, Math.round((metrics.totalResponses / maxResponses) * 100)) : 0;

    return metrics;
  };

  const sessionMetrics = calculateSessionMetrics();

  // Get subsessions from session data
  const subsessions = session.subSessions || [];

  return (
    <DashboardLayout title={session.name}>
      <div className="p-4 space-y-4">
        {/* Back Button */}
        <button
          onClick={() => navigate('/trainer/sessions')}
          className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors group"
        >
          <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm font-medium">Back to Sessions</span>
        </button>

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">{session.name}</h1>
              <Badge variant={session.status} dot={session.status === 'live'}>
                {session.status}
              </Badge>
            </div>
            <p className="text-neutral-600">
              {(() => {
                if (!session.startTime) return 'Not set';

                const start = new Date(session.startTime);
                const end = session.endTime ? new Date(session.endTime) : null;

                // Check if multi-day session (different dates)
                const isMultiDay = end && start.toISOString().slice(0, 10) !== end.toISOString().slice(0, 10);

                if (isMultiDay) {
                  // Multi-day: "20 Jan - 22 Jan 2026 • 10:15 am - 5:00 pm (daily)"
                  const startDateStr = formatDateWithTimezone(session.startTime, { day: 'numeric', month: 'short' });
                  const endDateStr = formatDateWithTimezone(session.endTime, { day: 'numeric', month: 'short', year: 'numeric' });
                  const startTimeStr = formatTime(session.startTime);
                  const endTimeStr = formatTime(session.endTime);
                  return `${startDateStr} - ${endDateStr} • ${startTimeStr} - ${endTimeStr} (daily)`;
                } else {
                  // Single-day: "20 Jan 2026 • 10:15 am - 5:00 pm"
                  const dateStr = formatDateWithTimezone(session.startTime, { day: 'numeric', month: 'short', year: 'numeric' });
                  const startTimeStr = formatTime(session.startTime);
                  const endTimeStr = end ? formatTime(session.endTime) : '--:--';
                  return `${dateStr} • ${startTimeStr} - ${endTimeStr}`;
                }
              })()}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-sm text-neutral-500">Code: <span className="font-bold text-primary-600">{session.code}</span></p>
              <button
                onClick={() => setShowQRModal(true)}
                className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h2M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
                Show QR Code
              </button>
            </div>
          </div>
          <div className="flex gap-2">
            {/* Available at every status: a scheduled session's report carries the
                pre-session readiness data trainers need before the session day. */}
            <Button variant="ghost" onClick={() => navigate(`/trainer/sessions/${session._id}/report`)}>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Session Report
            </Button>
            {session.status === 'scheduled' && (
              <>
                <Button variant="primary" onClick={handleStartSession}>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Start Session
                </Button>
                {soloTrainer && (
                  <Button variant="outline" onClick={() => navigate(`/trainer/sessions/${session._id}/edit`)}>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit Session
                  </Button>
                )}
                <Button variant="outline" onClick={() => navigate(`/trainer/sessions/${session._id}/setup`)}>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Setup Session
                </Button>
              </>
            )}
            {session.status === 'live' && (
              <>
                <Button variant="primary" onClick={() => navigate(`/trainer/session/${session._id}/control`)}>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Control Session
                </Button>
                <Button variant="outline" onClick={() => navigate(`/trainer/sessions/${session._id}/setup`)}>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Setup
                </Button>
              </>
            )}
            {session.status === 'completed' && (
              <>
                {/* TODO: Re-enable when backend endpoint is built
                <Button variant="outline" onClick={() => navigate(`/trainer/sessions/${session._id}/facilitation-feedback`)}>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  Facilitation Feedback
                </Button>
                */}
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-neutral-200">
          <div className="flex gap-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 px-1 text-sm font-medium transition-colors relative ${
                  activeTab === tab.id
                    ? 'text-primary-600'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600"></div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Session Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card title="Session Information" padding="lg">
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Organization</p>
                    <p className="text-neutral-900">{session.organization?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Session Type</p>
                    <p className="text-neutral-900">{session.sessionType || 'Public'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Expected Participants</p>
                    <p className="text-neutral-900">{session.participants?.filter(p => p.status === 'joined').length || session.currentParticipants || 0}/{session.expectedParticipants || session.maxParticipants}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Duration</p>
                    <p className="text-neutral-900">
                      {(() => {
                        // Check if multi-day session
                        if (session.startTime && session.endTime) {
                          const start = new Date(session.startTime);
                          const end = new Date(session.endTime);
                          const isMultiDay = start.toISOString().slice(0, 10) !== end.toISOString().slice(0, 10);

                          if (isMultiDay) {
                            // Calculate number of days
                            const startDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                            const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                            const diffTime = endDate - startDate;
                            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end day
                            return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
                          }
                        }

                        // Single-day: show hours/minutes
                        if (session.duration) {
                          return session.duration >= 60
                            ? `${Math.floor(session.duration / 60)}h ${session.duration % 60 > 0 ? `${session.duration % 60}m` : ''}`
                            : `${session.duration} minutes`;
                        }
                        return 'N/A';
                      })()}
                    </p>
                  </div>
                </div>
              </Card>

              <Card title="Preparation Checklist" padding="lg">
                <div className="space-y-3">
                  {/* Dynamic checklist based on session data */}
                  {(() => {
                    const hasDescription = !!(session.description && session.description.trim());
                    const hasSubsessions = subsessions.length > 0;
                    const totalActivities = subsessions.reduce((sum, s) => sum + (s.activities?.length || 0), 0);
                    const hasActivities = totalActivities > 0;
                    const hasWorkbooks = session.workbooks && session.workbooks.length > 0;

                    const checklistItems = [
                      {
                        label: 'Session details configured',
                        completed: hasDescription || session.name,
                        hint: !hasDescription && !session.name ? 'Add session description' : null
                      },
                      {
                        label: 'Create sub-sessions',
                        completed: hasSubsessions,
                        hint: !hasSubsessions ? 'Go to Setup to create sub-sessions' : null
                      },
                      {
                        label: 'Add activities to sub-sessions',
                        completed: hasActivities,
                        hint: !hasActivities ? `${hasSubsessions ? 'Add activities to your sub-sessions' : 'Create sub-sessions first'}` : null
                      },
                      {
                        label: 'Upload workbooks/materials',
                        completed: hasWorkbooks,
                        hint: !hasWorkbooks ? 'Optional: Upload session materials' : null
                      },
                      {
                        label: 'Review session setup',
                        completed: hasSubsessions && hasActivities,
                        hint: !(hasSubsessions && hasActivities) ? 'Complete all above steps first' : null
                      },
                    ];

                    const completedCount = checklistItems.filter(item => item.completed).length;

                    return (
                      <>
                        <div className="mb-3 pb-3 border-b border-neutral-200">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-neutral-600">Progress</span>
                            <span className="text-sm font-semibold text-neutral-900">{completedCount}/{checklistItems.length}</span>
                          </div>
                          <div className="mt-2 h-2 bg-neutral-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-green-500 rounded-full transition-all duration-300"
                              style={{ width: `${(completedCount / checklistItems.length) * 100}%` }}
                            />
                          </div>
                        </div>
                        {checklistItems.map((item, index) => (
                          <div key={index} className="flex items-center gap-3">
                            {item.completed ? (
                              <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            ) : (
                              <div className="w-5 h-5 bg-neutral-300 rounded-full flex items-center justify-center flex-shrink-0"></div>
                            )}
                            <div className="flex-1">
                              <p className={`text-sm ${item.completed ? 'text-neutral-700' : 'text-neutral-500'}`}>
                                {item.label}
                              </p>
                              {!item.completed && item.hint && (
                                <p className="text-xs text-neutral-400 mt-0.5">{item.hint}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </>
                    );
                  })()}
                </div>
              </Card>
            </div>

            {/* Engagement Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                label="Attendance Rate"
                value={`${session.maxParticipants > 0 ? Math.round(((session.participants?.filter(p => p.status === 'joined').length || 0) / session.maxParticipants) * 100) : 0}%`}
                icon={CheckCircle2}
                variant="emerald"
              />
              <StatCard label="Sub-sessions"     value={session.subSessions?.length || 0}                                                        icon={Layers}     variant="blue" />
              <StatCard label="Total Activities" value={session.subSessions?.reduce((total, sub) => total + (sub.activities?.length || 0), 0) || 0} icon={ListChecks} variant="violet" />
              <StatCard label="Workbooks"        value={session.workbooks?.length || 0}                                                          icon={BookOpen}   variant="slate" />
            </div>
          </div>
        )}

        {/* Pre-Session Tab — PreSessionAnalyticsTab matches the API contract
            (overview.totalParticipants / averageCompletion, materials,
            selfAssessment, challenges.total, completionTable). The inline copy
            that used to live here read different key names, so it showed 0 for
            every figure and hid the participant completion table. */}
        {activeTab === 'pre-session' && (
          <PreSessionAnalyticsTab sessionId={sessionId} />
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Summary Stats Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-neutral-200 p-4">
                <p className="text-xs text-neutral-500 font-medium mb-1">Total Activities</p>
                <p className="text-2xl font-bold text-neutral-900">
                  {session.subSessions?.reduce((sum, s) => sum + (s.activities?.length || 0), 0) || 0}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-neutral-200 p-4">
                <p className="text-xs text-neutral-500 font-medium mb-1">Total Challenges</p>
                <p className="text-2xl font-bold text-neutral-900">{sessionMetrics.totalChallenges}</p>
              </div>
              <div className="bg-white rounded-xl border border-neutral-200 p-4">
                <p className="text-xs text-neutral-500 font-medium mb-1">Total Action Plans</p>
                <p className="text-2xl font-bold text-neutral-900">{sessionMetrics.totalActionPlans}</p>
              </div>
              <div className="bg-white rounded-xl border border-neutral-200 p-4">
                <p className="text-xs text-neutral-500 font-medium mb-1">Engagement Rate</p>
                <p className="text-2xl font-bold text-green-600">{sessionMetrics.engagementRate}%</p>
              </div>
            </div>

            {/* Session Indices */}
            {sessionMetrics.hasData ? (
              <Card title="Session Performance Indices" padding="lg">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {/* Topic Relevance Index */}
                  {sessionMetrics.trs.value > 0 && (
                    <div className="flex flex-col items-center p-4 bg-neutral-50 rounded-xl">
                      <GaugeMeter
                        value={sessionMetrics.trs.value}
                        maxValue={5}
                        label="Topic Relevance"
                        band={sessionMetrics.trs.band?.label || 'N/A'}
                        color={sessionMetrics.trs.band?.color || 'gray'}
                        size="sm"
                      />
                      <p className="mt-2 text-xs text-neutral-500 text-center">
                        How relevant participants found the content
                      </p>
                    </div>
                  )}

                  {/* Trainer Effectiveness Index */}
                  {sessionMetrics.tei.value > 0 && (
                    <div className="flex flex-col items-center p-4 bg-neutral-50 rounded-xl">
                      <GaugeMeter
                        value={sessionMetrics.tei.value}
                        maxValue={5}
                        label="Trainer Effectiveness"
                        band={sessionMetrics.tei.band?.label || 'N/A'}
                        color={sessionMetrics.tei.band?.color || 'gray'}
                        size="sm"
                      />
                      <p className="mt-2 text-xs text-neutral-500 text-center">
                        Delivery and facilitation effectiveness
                      </p>
                    </div>
                  )}

                  {/* Participant Confidence Index */}
                  {sessionMetrics.pci.value > 0 && (
                    <div className="flex flex-col items-center p-4 bg-neutral-50 rounded-xl">
                      <GaugeMeter
                        value={sessionMetrics.pci.value}
                        maxValue={5}
                        label="Confidence Index"
                        band={sessionMetrics.pci.band?.label || 'N/A'}
                        color={sessionMetrics.pci.band?.color || 'gray'}
                        size="sm"
                      />
                      <p className="mt-2 text-xs text-neutral-500 text-center">
                        Participant confidence to apply learning
                      </p>
                    </div>
                  )}

                  {/* Learning Advocacy Index */}
                  {sessionMetrics.lai.value > 0 && (
                    <div className="flex flex-col items-center p-4 bg-neutral-50 rounded-xl">
                      <GaugeMeter
                        value={sessionMetrics.lai.value}
                        maxValue={5}
                        label="Learning Advocacy"
                        band={sessionMetrics.lai.band?.label || 'N/A'}
                        color={sessionMetrics.lai.band?.color || 'gray'}
                        size="sm"
                      />
                      <p className="mt-2 text-xs text-neutral-500 text-center">
                        Likelihood to recommend training
                      </p>
                    </div>
                  )}

                  {/* Challenge Resolution Index */}
                  {sessionMetrics.cri.value > 0 && (
                    <div className="flex flex-col items-center p-4 bg-neutral-50 rounded-xl">
                      <GaugeMeter
                        value={sessionMetrics.cri.value}
                        maxValue={5}
                        label="Challenge Resolution"
                        band={sessionMetrics.cri.band?.label || 'N/A'}
                        color={sessionMetrics.cri.band?.color || 'gray'}
                        size="sm"
                      />
                      <p className="mt-2 text-xs text-neutral-500 text-center">
                        How well challenges were addressed
                      </p>
                    </div>
                  )}

                  {/* Action Readiness Index */}
                  {sessionMetrics.ari.value > 0 && (
                    <div className="flex flex-col items-center p-4 bg-neutral-50 rounded-xl">
                      <GaugeMeter
                        value={sessionMetrics.ari.value}
                        maxValue={1}
                        label="Action Readiness"
                        band={sessionMetrics.ari.band?.label || 'N/A'}
                        color={sessionMetrics.ari.band?.color || 'gray'}
                        size="sm"
                      />
                      <p className="mt-2 text-xs text-neutral-500 text-center">
                        Participant readiness to take action
                      </p>
                    </div>
                  )}
                </div>
              </Card>
            ) : (
              <Card padding="lg">
                <div className="text-center py-8">
                  <svg className="w-12 h-12 text-neutral-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <h3 className="text-lg font-medium text-neutral-700 mb-1">No Analytics Data Yet</h3>
                  <p className="text-sm text-neutral-500">
                    Analytics will appear once participants complete feedback activities
                  </p>
                </div>
              </Card>
            )}

            {/* View Full Report Button */}
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => navigate(`/session/${session._id}/report`)}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                View Full Session Report
              </Button>
            </div>
          </div>
        )}

        {/* Sub-sessions Tab */}
        {activeTab === 'subsessions' && (
          <div className="space-y-4">
            <Card padding="none">
              <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-neutral-900">Sub-sessions</h3>
                  <p className="text-sm text-neutral-600">Manage your session structure</p>
                </div>
                {session.status === 'scheduled' && (
                  <Button variant="primary" size="sm" onClick={() => navigate(`/trainer/sessions/${session._id}/setup`)}>
                    Edit Sub-sessions
                  </Button>
                )}
              </div>
              {subsessions.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-neutral-100 rounded-full mb-4">
                    <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">No Sub-sessions Yet</h3>
                  <p className="text-neutral-600 mb-4">Create sub-sessions to structure your training session</p>
                  {session.status === 'scheduled' && (
                    <Button variant="primary" onClick={() => navigate(`/trainer/sessions/${session._id}/setup`)}>
                      Create Sub-sessions
                    </Button>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-neutral-100">
                  {subsessions.map((subsession, index) => (
                  <div key={subsession._id} className="p-6 hover:bg-neutral-50 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0">
                          {index + 1}
                        </div>
                        <div>
                          <h4 className="font-semibold text-neutral-900 mb-1">{subsession.name || subsession.title}</h4>
                          <p className="text-sm text-neutral-600">
                            {(subsession.startTime && subsession.endTime) ? `${subsession.duration || Math.round((new Date(subsession.endTime) - new Date(subsession.startTime)) / 60000)} minutes • ` : ''}{subsession.activities?.length || 0} activities
                          </p>
                        </div>
                      </div>
                      <Badge variant={subsession.status === 'completed' ? 'success' : subsession.status === 'active' ? 'info' : 'default'}>
                        {subsession.status || 'pending'}
                      </Badge>
                    </div>
                    {subsession.activities && subsession.activities.length > 0 && (
                      <div className="ml-13 mt-3 space-y-2">
                        <p className="text-xs font-semibold text-neutral-600 mb-2">Activities:</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {subsession.activities.map((activity) => (
                            <div key={activity.id} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 px-3 py-2 rounded-lg">
                              <span className={`w-6 h-6 flex-shrink-0 rounded-md flex items-center justify-center ${getActivityTypeColor(activity.type)}`}>
                                {getActivityIcon(activity.type, 'sm')}
                              </span>
                              <span className="font-medium capitalize">{activity.type}:</span>
                              <span className="truncate">{activity.title || activity.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* AI Impact Tab */}
        {activeTab === 'ai-report' && (
          <div className="space-y-6">
            {/* Header with Generate button */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-neutral-900">AI Impact Reports</h2>
                <p className="text-sm text-neutral-500">AI-generated analysis of session data</p>
              </div>
              <button
                onClick={async () => {
                  setAiReportLoading(true);
                  setAiReport(null);
                  try {
                    const res = await api.post(`/ai/reports/session/${sessionId}`, {});
                    if (res.success && !res.data?.error) {
                      setAiReport(res.data);
                      // Refresh history
                      try {
                        const histRes = await api.get(`/ai/reports/session/${sessionId}/history`);
                        if (histRes.success) setAiHistory(histRes.data);
                      } catch {}
                    } else {
                      // Prefer the backend's friendly userMessage (rate-limit /
                      // high-demand) over the raw error code/stack trace.
                      setAiReport({
                        error: res.data?.error || 'AI not available',
                        userMessage: res.data?.userMessage,
                      });
                    }
                  } catch (err) {
                    setAiReport({ error: err.message });
                  } finally {
                    setAiReportLoading(false);
                  }
                }}
                disabled={aiReportLoading}
                className="px-6 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-bold hover:bg-primary-600 transition-all shadow-lg shadow-primary-200 disabled:opacity-50 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                </svg>
                {aiReportLoading ? 'Generating...' : 'Generate New Report'}
              </button>
            </div>

            {/* Report History */}
            {aiHistory.length > 0 && !aiReport && !aiReportLoading && (
              <Card padding="md">
                <h3 className="text-sm font-semibold text-neutral-700 mb-3">Previous Reports</h3>
                <div className="space-y-2">
                  {aiHistory.map((report) => (
                    <button
                      key={report._id}
                      onClick={async () => {
                        setAiReportLoading(true);
                        try {
                          const res = await api.get(`/ai/reports/${report._id}`);
                          if (res.success) setAiReport(res.data);
                        } catch (err) {
                          console.error('Error loading report:', err);
                        } finally {
                          setAiReportLoading(false);
                        }
                      }}
                      className="w-full flex items-start gap-3 p-3 bg-neutral-50 hover:bg-neutral-100 rounded-xl border border-neutral-200 transition-colors text-left"
                    >
                      <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-neutral-900">
                            {new Date(report.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            {' at '}
                            {new Date(report.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                          <svg className="w-4 h-4 text-neutral-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                          </svg>
                        </div>
                        <p className="text-xs text-neutral-500 mt-0.5">By {report.generatedBy}</p>
                        {report.summary && <p className="text-xs text-neutral-400 mt-1 line-clamp-2">{report.summary}</p>}
                      </div>
                    </button>
                  ))}
                </div>
              </Card>
            )}

            {/* Empty state */}
            {!aiReport && !aiReportLoading && aiHistory.length === 0 && !aiHistoryLoading && (
              <Card padding="lg">
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-2">No Reports Yet</h3>
                  <p className="text-sm text-neutral-500 max-w-md mx-auto">
                    Click "Generate New Report" to create an AI-powered analysis. All reports are saved and can be viewed anytime.
                  </p>
                </div>
              </Card>
            )}

            {aiReportLoading && (
              <Card padding="lg">
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mx-auto mb-4"></div>
                  <p className="text-sm text-primary-600 font-medium">AI is analyzing session data...</p>
                  <p className="text-xs text-neutral-400 mt-1">This may take 10-20 seconds</p>
                </div>
              </Card>
            )}

            {aiReport?.error && (
              <Card padding="lg">
                <div className="text-center py-8">
                  <p className="text-lg font-bold text-neutral-900 mb-2">AI Not Available</p>
                  <p className="text-sm text-neutral-500 mb-4">{aiReport.userMessage || (typeof aiReport.error === 'string' && !['rate_limited', 'overloaded'].includes(aiReport.error) ? aiReport.error : 'Aktivity Intelligence™ (AI) is temporarily unavailable. Please try again in a few minutes.')}</p>
                  <button onClick={() => setAiReport(null)} className="px-6 py-2 bg-neutral-100 text-neutral-700 rounded-lg text-sm font-medium hover:bg-neutral-200">Try Again</button>
                </div>
              </Card>
            )}

            {aiReport && !aiReport.error && (
              <div className="space-y-4">
                {/* Report header with back link */}
                {aiReport.createdAt && (
                  <div className="flex items-center gap-2 text-xs text-neutral-500">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Generated on {new Date(aiReport.createdAt).toLocaleString('en-IN')}
                    {aiReport.generatedBy && ` by ${aiReport.generatedBy}`}
                    <button
                      onClick={() => setAiReport(null)}
                      className="ml-auto text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Back to History
                    </button>
                  </div>
                )}

                {/* Executive Summary */}
                {aiReport.executiveSummary && (
                  <Card padding="lg">
                    <h3 className="text-lg font-bold text-neutral-900 mb-3">Executive Summary</h3>
                    <p className="text-sm text-neutral-700 leading-relaxed">{aiReport.executiveSummary}</p>
                  </Card>
                )}

                {/* Pre-Session Readiness (for scheduled sessions) */}
                {aiReport.preSessionReadiness && (
                  <Card padding="lg">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-neutral-900">Pre-Session Readiness</h3>
                      <Badge variant={aiReport.preSessionReadiness.overallScore === 'excellent' || aiReport.preSessionReadiness.overallScore === 'high' ? 'success' : aiReport.preSessionReadiness.overallScore === 'moderate' ? 'warning' : 'secondary'}>
                        {aiReport.preSessionReadiness.overallScore}
                      </Badge>
                    </div>
                    <div className="space-y-3">
                      {aiReport.preSessionReadiness.materialCompletion && (
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-xs text-blue-600 font-medium mb-1">Material Completion</p>
                          <p className="text-sm text-blue-900">{aiReport.preSessionReadiness.materialCompletion}</p>
                        </div>
                      )}
                      {aiReport.preSessionReadiness.selfAssessmentInsights && (
                        <div className="p-3 bg-green-50 rounded-lg">
                          <p className="text-xs text-green-600 font-medium mb-1">Self-Assessment Insights</p>
                          <p className="text-sm text-green-900">{aiReport.preSessionReadiness.selfAssessmentInsights}</p>
                        </div>
                      )}
                      {aiReport.preSessionReadiness.challengeAnalysis && (
                        <div className="p-3 bg-amber-50 rounded-lg">
                          <p className="text-xs text-amber-600 font-medium mb-1">Participant Workplace Challenges</p>
                          <p className="text-sm text-amber-900">{aiReport.preSessionReadiness.challengeAnalysis}</p>
                        </div>
                      )}
                      {aiReport.preSessionReadiness.expectationAlignment && (
                        <div className="p-3 bg-purple-50 rounded-lg">
                          <p className="text-xs text-purple-600 font-medium mb-1">Expectation Alignment</p>
                          <p className="text-sm text-purple-900">{aiReport.preSessionReadiness.expectationAlignment}</p>
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                {/* Trainer Preparation (for scheduled sessions) */}
                {aiReport.trainerPreparation && (
                  <Card padding="lg">
                    <h3 className="text-lg font-bold text-neutral-900 mb-3">Trainer Preparation Guide</h3>
                    <p className="text-sm text-neutral-700 mb-4">{aiReport.trainerPreparation.summary}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {aiReport.trainerPreparation.topChallengeThemes?.length > 0 && (
                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                          <p className="text-xs text-amber-700 font-bold mb-2">Challenge Themes to Address</p>
                          {aiReport.trainerPreparation.topChallengeThemes.map((t, i) => (
                            <p key={i} className="text-sm text-amber-800 mb-1">- {t}</p>
                          ))}
                        </div>
                      )}
                      {aiReport.trainerPreparation.suggestedFocusAreas?.length > 0 && (
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                          <p className="text-xs text-blue-700 font-bold mb-2">Suggested Focus Areas</p>
                          {aiReport.trainerPreparation.suggestedFocusAreas.map((a, i) => (
                            <p key={i} className="text-sm text-blue-800 mb-1">- {a}</p>
                          ))}
                        </div>
                      )}
                    </div>
                    {aiReport.trainerPreparation.customizationTips?.length > 0 && (
                      <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-100">
                        <p className="text-xs text-green-700 font-bold mb-2">Customization Tips</p>
                        {aiReport.trainerPreparation.customizationTips.map((t, i) => (
                          <p key={i} className="text-sm text-green-800 mb-1">- {t}</p>
                        ))}
                      </div>
                    )}
                  </Card>
                )}

                {/* Training Effectiveness (for live/completed sessions) */}
                {aiReport.trainingEffectiveness && (
                  <Card padding="lg">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-neutral-900">Training Effectiveness</h3>
                      <Badge variant={aiReport.trainingEffectiveness.overallScore === 'excellent' || aiReport.trainingEffectiveness.overallScore === 'high' ? 'success' : aiReport.trainingEffectiveness.overallScore === 'moderate' ? 'warning' : 'secondary'}>
                        {aiReport.trainingEffectiveness.overallScore}
                      </Badge>
                    </div>
                    <div className="space-y-3">
                      {aiReport.trainingEffectiveness.evidence && (
                        <div className="p-3 bg-neutral-50 rounded-lg">
                          <p className="text-xs text-neutral-500 font-medium mb-1">Evidence</p>
                          <p className="text-sm text-neutral-700">{aiReport.trainingEffectiveness.evidence}</p>
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {aiReport.trainingEffectiveness.preSessionEngagement && (
                          <div className="p-3 bg-blue-50 rounded-lg">
                            <p className="text-xs text-blue-600 font-medium mb-1">Pre-Session</p>
                            <p className="text-sm text-blue-900">{aiReport.trainingEffectiveness.preSessionEngagement}</p>
                          </div>
                        )}
                        {aiReport.trainingEffectiveness.inSessionEngagement && (
                          <div className="p-3 bg-green-50 rounded-lg">
                            <p className="text-xs text-green-600 font-medium mb-1">In-Session</p>
                            <p className="text-sm text-green-900">{aiReport.trainingEffectiveness.inSessionEngagement}</p>
                          </div>
                        )}
                        {aiReport.trainingEffectiveness.postSessionApplication && (
                          <div className="p-3 bg-purple-50 rounded-lg">
                            <p className="text-xs text-purple-600 font-medium mb-1">Post-Session</p>
                            <p className="text-sm text-purple-900">{aiReport.trainingEffectiveness.postSessionApplication}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                )}

                {/* Challenge Resolution (completed sessions) */}
                {aiReport.challengeResolution && (
                  <Card padding="lg">
                    <h3 className="text-lg font-bold text-neutral-900 mb-3">Challenge Resolution</h3>
                    <p className="text-sm text-neutral-700 mb-3">{aiReport.challengeResolution.summary}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {aiReport.challengeResolution.topChallengesAddressed?.length > 0 && (
                        <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                          <p className="text-xs text-green-600 font-bold mb-2">Addressed</p>
                          {aiReport.challengeResolution.topChallengesAddressed.map((c, i) => (
                            <p key={i} className="text-sm text-green-800 mb-1">- {c}</p>
                          ))}
                        </div>
                      )}
                      {aiReport.challengeResolution.unaddressedGaps?.length > 0 && (
                        <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                          <p className="text-xs text-red-600 font-bold mb-2">Gaps Remaining</p>
                          {aiReport.challengeResolution.unaddressedGaps.map((c, i) => (
                            <p key={i} className="text-sm text-red-800 mb-1">- {c}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                {/* Action Plan Analysis (completed sessions) */}
                {aiReport.actionPlanAnalysis && (
                  <Card padding="lg">
                    <h3 className="text-lg font-bold text-neutral-900 mb-3">Action Plan Analysis</h3>
                    <div className="space-y-3">
                      {['qualityAssessment', 'completionTrend', 'blockerPatterns', 'managerEngagement'].map(key => (
                        aiReport.actionPlanAnalysis[key] && (
                          <div key={key} className="p-3 bg-neutral-50 rounded-lg">
                            <p className="text-xs text-neutral-500 font-medium mb-1">{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</p>
                            <p className="text-sm text-neutral-700">{aiReport.actionPlanAnalysis[key]}</p>
                          </div>
                        )
                      ))}
                    </div>
                  </Card>
                )}

                {/* Key Metrics */}
                {aiReport.keyMetrics && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {aiReport.keyMetrics.learningTransferRate && (
                      <Card padding="md">
                        <p className="text-xs text-neutral-500 font-medium mb-1">Learning Transfer Rate</p>
                        <p className="text-lg font-bold text-primary-600">{aiReport.keyMetrics.learningTransferRate}</p>
                      </Card>
                    )}
                    {aiReport.keyMetrics.managerSupportRate && (
                      <Card padding="md">
                        <p className="text-xs text-neutral-500 font-medium mb-1">Manager Support Rate</p>
                        <p className="text-lg font-bold text-green-600">{aiReport.keyMetrics.managerSupportRate}</p>
                      </Card>
                    )}
                    {aiReport.keyMetrics.sustainedEngagement && (
                      <Card padding="md">
                        <p className="text-xs text-neutral-500 font-medium mb-1">Sustained Engagement</p>
                        <p className="text-lg font-bold text-blue-600">{aiReport.keyMetrics.sustainedEngagement}</p>
                      </Card>
                    )}
                  </div>
                )}

                {/* Strengths & Improvements */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {aiReport.strengths?.length > 0 && (
                    <Card padding="lg">
                      <h3 className="text-base font-bold text-green-800 mb-3">Strengths</h3>
                      <div className="space-y-2">
                        {aiReport.strengths.map((s, i) => (
                          <div key={i} className="flex items-start gap-2 p-2 bg-green-50 rounded-lg">
                            <svg className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                            <p className="text-sm text-green-800">{s}</p>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}
                  {aiReport.improvements?.length > 0 && (
                    <Card padding="lg">
                      <h3 className="text-base font-bold text-amber-800 mb-3">Areas for Improvement</h3>
                      <div className="space-y-2">
                        {aiReport.improvements.map((s, i) => (
                          <div key={i} className="flex items-start gap-2 p-2 bg-amber-50 rounded-lg">
                            <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" /></svg>
                            <p className="text-sm text-amber-800">{s}</p>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}
                </div>

                {/* Key Insights (flat list returned by some models) */}
                {Array.isArray(aiReport.insights) && aiReport.insights.length > 0 && (
                  <Card padding="lg">
                    <h3 className="text-lg font-bold text-neutral-900 mb-3">Key Insights</h3>
                    <div className="space-y-2">
                      {aiReport.insights.map((s, i) => {
                        const text = typeof s === 'string' ? s : (s.text || s.insight || '');
                        if (!text) return null;
                        return (
                          <div key={i} className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-100">
                            <span className="w-6 h-6 rounded-full bg-primary-200 text-primary-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                            <p className="text-sm text-neutral-700">{text}</p>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                )}

                {/* Recommendations */}
                {aiReport.recommendations?.length > 0 && (
                  <Card padding="lg">
                    <h3 className="text-lg font-bold text-neutral-900 mb-3">Recommendations</h3>
                    <div className="space-y-3">
                      {aiReport.recommendations.map((r, i) => {
                        // Recommendations may arrive as plain strings or as
                        // { recommendation, priority, expectedImpact } objects.
                        const isStr = typeof r === 'string';
                        const text = isStr ? r : (r.recommendation || r.title || r.text || '');
                        if (!text) return null;
                        const priority = isStr ? '' : (r.priority || '');
                        const expectedImpact = isStr ? '' : r.expectedImpact;
                        return (
                          <div key={i} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-neutral-200">
                            {priority ? (
                              <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase flex-shrink-0 ${
                                priority === 'high' ? 'bg-red-100 text-red-700' :
                                priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>{priority}</span>
                            ) : (
                              <span className="w-6 h-6 rounded-full bg-primary-200 text-primary-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                            )}
                            <div>
                              <p className="text-sm font-medium text-neutral-900">{text}</p>
                              {expectedImpact && <p className="text-xs text-neutral-500 mt-1">{expectedImpact}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                )}

                {/* Next Steps */}
                {aiReport.nextSteps?.length > 0 && (
                  <Card padding="lg">
                    <h3 className="text-lg font-bold text-neutral-900 mb-3">Immediate Next Steps</h3>
                    <div className="space-y-2">
                      {aiReport.nextSteps.map((step, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-primary-50 rounded-lg border border-primary-100">
                          <span className="w-6 h-6 rounded-full bg-primary-200 text-primary-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                          <p className="text-sm text-primary-900">{step}</p>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

              </div>
            )}
          </div>
        )}

      </div>

      {/* Early Start Warning Modal */}
      <Modal
        isOpen={earlyStartModal.show}
        onClose={() => setEarlyStartModal({ show: false, timeRemaining: '' })}
        title="Session Not Ready to Start"
        size="sm"
      >
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">
            Please Wait
          </h3>
          <p className="text-neutral-600 mb-4">
            This session is scheduled to start at <span className="font-semibold text-primary-600">{earlyStartModal.scheduledTime}</span>.
          </p>
          <p className="text-neutral-600">
            Time remaining: <span className="font-bold text-primary-600">{earlyStartModal.timeRemaining}</span>
          </p>
          <div className="mt-6">
            <Button
              variant="primary"
              onClick={() => setEarlyStartModal({ show: false, timeRemaining: '' })}
            >
              Understood
            </Button>
          </div>
        </div>
      </Modal>

      {/* QR Code Modal */}
      <Modal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        title="Session QR Code"
        size="lg"
      >
        <QRCodeGenerator
          sessionCode={session?.code}
          sessionName={session?.name}
          size={256}
          showDownload={true}
        />
      </Modal>

    </DashboardLayout>
  );
};

export default TrainerSessionDetailPage;
